import classNames from "classnames";
import { format as formatDate } from "date-fns";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import { EditablePostalCode } from "../../../components/EditablePostalCode";
import { ILocation } from "../../../models/location";
import { preferredLocationSelector } from "../../../store/common/selectors";
import { useAppSelector } from "../../../store/reducers";
import {
    ABTest,
    ABTestVariant,
    getDesiredABTestVariant,
} from "../../../utils/abTesting";
import { isZipLocation } from "../../../utils/guards";
import { PredictedDeliveryLoader } from "../PredictedDeliveryLoader";
import {
    PredictedDeliveryDateDisplayType,
    PredictedDeliveryDateStatus,
    PredictedDeliveryDateStyle,
} from "../constants";
import { IDeliveryDatePredictionState } from "../reducers.interfaces";

import styles from "./index.module.scss";

const shouldReloadPrediction = (
    prediction: IDeliveryDatePredictionState,
    location: ILocation | null,
): boolean => {
    return (
        prediction.status === PredictedDeliveryDateStatus.ERROR &&
        location !== null &&
        isZipLocation(location) &&
        prediction.data.postcode !== location.zip
    );
};

const isApiOfflineError = (
    prediction: IDeliveryDatePredictionState,
): boolean => {
    return (
        prediction.status === PredictedDeliveryDateStatus.ERROR &&
        prediction.data.status_code === "deliverydates-api-offline-error"
    );
};

const isUnpredictableLocationError = (
    prediction: IDeliveryDatePredictionState,
): boolean => {
    return (
        prediction.status === PredictedDeliveryDateStatus.ERROR &&
        prediction.data.status_code === "unpredictable-location-error"
    );
};

const getDisplayDate = (
    prediction: Extract<
        IDeliveryDatePredictionState,
        { status: PredictedDeliveryDateStatus.LOADED }
    >,
    type?: PredictedDeliveryDateDisplayType,
): string => {
    return type === PredictedDeliveryDateDisplayType.SHOW_AGGRESSIVE
        ? prediction.data.date_aggressive
        : prediction.data.date_conservative;
};

const getDeliveryTextFormat = (deliveryIsFree?: boolean): string => {
    // TODO(i18n): Missing whitespace in translation string - should be "Delivery to <Location /> as soon as" with space after "to"
    return deliveryIsFree === false
        ? t`Estimated Delivery to<Location />as soon as <Date />`
        : t`Free Delivery to<Location />as soon as <Date />`;
};

export const PredictedDeliveryDate = (
    props: Immutable<{
        type?: PredictedDeliveryDateDisplayType;
        isReadOnly?: boolean;
        style: PredictedDeliveryDateStyle;
        deliveryIsFree?: boolean;
    }>,
): React.ReactNode => {
    const location = useAppSelector(preferredLocationSelector);
    const prediction = useAppSelector(
        (state) => state.checkout.data.predicted_delivery_date,
    );

    // If AB test 17966 is set to B, don't show anything. If it's either A or not set, continue.
    if (
        getDesiredABTestVariant(ABTest.PREDICTED_DELIVERY_DATES) ===
        ABTestVariant.B
    ) {
        return null;
    }

    // If PDDs are disabled, still load the data.
    // NOTE:
    // This is temporary to assist with tailoring the Convey predictions
    // Should be removed once feature is used more heavily in production
    if (props.type === PredictedDeliveryDateDisplayType.DISABLED) {
        return <PredictedDeliveryLoader />;
    }

    // If prediction is not yet available or data status is initial, load data
    if (
        !prediction ||
        prediction.status === PredictedDeliveryDateStatus.INITIAL
    ) {
        return <PredictedDeliveryLoader />;
    }

    // If the loaded data indicates a convey error, but the detected
    // location has changed since the Convey data was loaded, load the
    // Convey data again for the newly detected location.
    if (shouldReloadPrediction(prediction, location)) {
        return <PredictedDeliveryLoader />;
    }

    // If the loaded data indicates that the convey API is offline, show nothing.
    if (isApiOfflineError(prediction)) {
        return null;
    }

    // If the loaded data indicates that the detected location can not be
    // predicted by the deliverydates API, show an error message.
    if (isUnpredictableLocationError(prediction)) {
        return (
            // Hide the message for now when the detected location can not be predicted
            // https://thelabnyc.plan.io/issues/22478#note-6
            <div className={styles.root} hidden={true}>
                <PredictedDeliveryLoader />
                {t`Due to your location, we're unable to provide an estimated
                    delivery date. However, after you place your order, a local
                    shipping agent will contact you to schedule delivery.`}
            </div>
        );
    }

    // For any other errors, show an error message.
    if (prediction.status === PredictedDeliveryDateStatus.ERROR) {
        return (
            <div className={styles.root}>
                {t`Please enter a valid ZIP Code:`}
                <EditablePostalCode isReadOnly={props.isReadOnly} />
            </div>
        );
    }

    // If the PDD is loading, render an empty div. This prevents page reflows/jumps since it
    // will take up the same vertical space as the fully-rendered component.
    if (prediction.status === PredictedDeliveryDateStatus.LOADING) {
        return <div className={styles.root}>&nbsp;</div>;
    }

    // We have loaded data, to render the PDD.
    // TypeScript's control flow analysis knows prediction.status is LOADED at this point
    const displayDate = getDisplayDate(prediction, props.type);

    const deliveryTextClasses = classNames({
        [styles.oneLine]: props.style === PredictedDeliveryDateStyle.ONE_LINE,
        [styles.twoLine]: props.style === PredictedDeliveryDateStyle.TWO_LINE,
    });

    const deliveryTextFormat = getDeliveryTextFormat(props.deliveryIsFree);

    return (
        <div className={styles.root}>
            <PredictedDeliveryLoader />
            <div className={deliveryTextClasses}>
                <span>
                    {richT(deliveryTextFormat, {
                        Location: () => (
                            <EditablePostalCode isReadOnly={props.isReadOnly} />
                        ),
                        Date: () => (
                            // TODO(i18n): Hard-coded date format "MMMM do" may not work for all locales; consider locale-aware date formatting
                            <span>{formatDate(displayDate, "MMMM do")}</span>
                        ),
                    })}
                </span>
            </div>
        </div>
    );
};
